import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useDocumentTitle } from "../../../../utils";
import usePeriod from "./hooks/usePeriod";
import { BdApiContext, BdSourcesContext, useBdQuery } from "./hooks/useBdApi";
import { relativeTime } from "./metrics";
import PeriodPicker from "./components/PeriodPicker";
import SyncStatus from "./components/SyncStatus";
import SearchBox from "./components/SearchBox";
import OverviewPage from "./pages/OverviewPage";
import TeamPage from "./pages/TeamPage";
import MemberPage from "./pages/MemberPage";
import AgpOverviewPage from "./agp/pages/AgpOverviewPage";
import AgpTeamPage from "./agp/pages/AgpTeamPage";
import AgpMemberPage from "./agp/pages/AgpMemberPage";
import { useAgpQuery, useAgpYear } from "./agp/hooks/useAgpApi";
import { YearSelector } from "./agp/components/AgpUi";
import { teamShortName } from "./metrics";

function Breadcrumbs({ meta, search, agpSearch }) {
  const location = useLocation();
  const parts = location.pathname.replace(/^\/landmark\/bd\/?/, "").split("/").filter(Boolean);
  const isAgp = parts[0] === "agp";
  const rest = isAgp ? parts.slice(1) : parts;
  const base = isAgp ? "/landmark/bd/agp" : "/landmark/bd";
  const qs = isAgp ? agpSearch : search;
  const crumbs = [{ label: "BD Performance", to: `/landmark/bd${search}` }];
  if (isAgp) crumbs.push({ label: "AGP Goals", to: `/landmark/bd/agp${agpSearch}` });

  if (rest[0] === "team" && rest[1]) {
    const team = meta && meta.teams ? meta.teams.find((t) => t.key === rest[1]) : null;
    crumbs.push({ label: team ? team.name : "Team", to: `${base}/team/${rest[1]}${qs}` });
  } else if (rest[0] === "member" && rest[1]) {
    const member = meta && meta.members ? meta.members.find((m) => m.sfUserId === rest[1]) : null;
    if (member && member.team) {
      crumbs.push({ label: member.team, to: `${base}/team/${member.teamKey}${qs}` });
    }
    crumbs.push({ label: member ? member.name : "Member", to: `${base}/member/${rest[1]}${qs}` });
  }

  return (
    <nav className="bd-crumbs" aria-label="Breadcrumb">
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        return (
          <span key={c.to} className="bd-crumbs__item">
            {i > 0 && <span className="bd-crumbs__sep">/</span>}
            {last ? <span className="bd-crumbs__current">{c.label}</span> : <Link to={c.to}>{c.label}</Link>}
          </span>
        );
      })}
    </nav>
  );
}

function TeamRouteTitle() {
  const { teamKey } = useParams();
  useDocumentTitle(`${teamShortName(teamKey)} · BD Performance`);
  return null;
}

// Collapsible list of every Salesforce report the dashboard is built from.
function DataSourcesPanel({ sources, sync }) {
  if (!sources || !sources.reports) return null;
  const reports = Object.values(sources.reports).filter((r) => r.isReport !== false);
  const other = Object.values(sources.reports).filter((r) => r.isReport === false);
  const lastSync = sync && sync.lastSuccess ? sync.lastSuccess.finishedAt || sync.lastSuccess.startedAt : null;
  return (
    <div className="bd-sources-panel">
      <details>
        <summary>
          Data sources
          <small>
            · {reports.length} Salesforce reports{lastSync ? ` · synced ${relativeTime(lastSync)}` : ""} · hover any <b>i</b> on the dashboard to see where a number comes from
          </small>
        </summary>
        <div className="bd-sources-panel__grid">
          {reports.map((r) => (
            <a key={r.key} className="bd-sources-panel__item" href={r.url} target="_blank" rel="noopener noreferrer">
              <strong>{r.name}</strong>
              <span>
                {r.doc || r.objectLabel} · {r.objectLabel}
              </span>
              <span>{r.filterText}</span>
            </a>
          ))}
        </div>
        {other.length > 0 && (
          <div className="bd-sources-panel__note">
            Also queried directly (no BD report):{" "}
            {other.map((r, i) => (
              <React.Fragment key={r.key}>
                {i > 0 && ", "}
                <a href={r.url} target="_blank" rel="noopener noreferrer">
                  {r.object}
                </a>{" "}
                ({r.filterText})
              </React.Fragment>
            ))}
            . AGP targets are set in BetterBlocks; AGP progress is measured from Jan 1 of the goal year to today.
          </div>
        )}
      </details>
    </div>
  );
}

// The app shell forces the template's dark theme (body[data-theme-version="dark"]),
// whose global overrides (white headings, dark inputs/tables, etc.) fight this
// dashboard's own styling. Switch the body to the light theme while mounted and
// restore the previous value on unmount.
function useLightTheme() {
  useEffect(() => {
    const body = document.body;
    const previous = body.getAttribute("data-theme-version");
    body.setAttribute("data-theme-version", "light");
    body.setAttribute("data-bd-dashboard", "true");
    return () => {
      if (previous) body.setAttribute("data-theme-version", previous);
      else body.removeAttribute("data-theme-version");
      body.removeAttribute("data-bd-dashboard");
    };
  }, []);
}

export default function BdDashboard() {
  useDocumentTitle("BD Performance");
  useLightTheme();
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  const ctx = useMemo(() => ({ version, bump }), [version, bump]);

  return (
    <BdApiContext.Provider value={ctx}>
      <Shell />
    </BdApiContext.Provider>
  );
}

function Shell() {
  const agpYear = useAgpYear();
  const location = useLocation();
  const isAgp = /^\/landmark\/bd\/agp(\/|$)/.test(location.pathname);
  const metaParams = useMemo(() => ({}), []);
  const meta = useBdQuery("meta", metaParams);
  const metaData = meta.data;
  const period = usePeriod(metaData ? metaData.dataWindow : null);
  const agpMeta = useAgpQuery("meta", metaParams);
  // the LocalRuntimeIndicator (dev only) is fixed to the top-right corner and
  // would otherwise sit on top of the Refresh button
  const isLocalDev = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  const sourcesCtx = useMemo(
    () => ({ sources: metaData ? metaData.sources : null, sync: metaData ? metaData.sync : null }),
    [metaData]
  );

  return (
    <BdSourcesContext.Provider value={sourcesCtx}>
    <div className="bd-wrapper">
      <header className="bd-topbar">
        <div className={`bd-topbar__inner ${isLocalDev ? "bd-topbar__inner--dev-offset" : ""}`}>
          <div className="bd-topbar__left">
            <Link to={`/landmark/bd${period.search}`} className="bd-brand" aria-label="BD Performance home">
              <span className="bd-brand__mark">BD</span>
            </Link>
            <nav className="bd-nav" aria-label="Sections">
              <NavLink to={`/landmark/bd${period.search}`} end className={({ isActive }) => `bd-nav__link ${isActive || (!isAgp && location.pathname !== "/landmark/bd") ? "is-active" : ""}`}>
                Performance
              </NavLink>
              <NavLink to={`/landmark/bd/agp${agpYear.search}`} className={() => `bd-nav__link ${isAgp ? "is-active" : ""}`}>
                AGP Goals
              </NavLink>
            </nav>
            <Breadcrumbs meta={metaData} search={period.search} agpSearch={agpYear.search} />
          </div>
          <div className="bd-topbar__right">
            <SearchBox meta={metaData} search={isAgp ? agpYear.search : period.search} basePath={isAgp ? "/landmark/bd/agp" : "/landmark/bd"} />
            {isAgp ? (
              <YearSelector year={agpYear.year} years={agpMeta.data ? agpMeta.data.years : []} onChange={agpYear.setYear} />
            ) : (
              <PeriodPicker period={period} />
            )}
            <SyncStatus initial={metaData ? metaData.sync : null} />
          </div>
        </div>
      </header>

      <main className="bd-main">
        <Routes>
          <Route index element={<OverviewPage period={period} meta={metaData} />} />
          <Route
            path="team/:teamKey"
            element={
              <>
                <TeamRouteTitle />
                <TeamPage period={period} meta={metaData} />
              </>
            }
          />
          <Route path="member/:sfUserId" element={<MemberPage period={period} meta={metaData} />} />
          <Route path="agp" element={<AgpOverviewPage agpYear={agpYear} agpMeta={agpMeta.data} />} />
          <Route path="agp/team/:teamKey" element={<AgpTeamPage agpYear={agpYear} agpMeta={agpMeta.data} />} />
          <Route path="agp/member/:sfUserId" element={<AgpMemberPage agpYear={agpYear} agpMeta={agpMeta.data} />} />
          <Route path="*" element={<Navigate to={`/landmark/bd${period.search}`} replace />} />
        </Routes>
      </main>

      <DataSourcesPanel sources={metaData ? metaData.sources : null} sync={metaData ? metaData.sync : null} />
      <footer className="bd-footer">
        <span>
          Source: Salesforce reports supplied by the BD team (see Data sources above) · Workplan points counted by{" "}
          {period.dateBasis === "accredited" ? "Accredited Date" : "Booked On"} · Default view: last week (Sun–Sat)
        </span>
        <span>Synced daily at 7:00 AM ET</span>
      </footer>
    </div>
    </BdSourcesContext.Provider>
  );
}
